/* eslint-disable no-console */
import { Ability } from '@casl/ability';
import { PaginatedQueryResult, useTableState } from '@dispatch/shared/hooks';
import {
  Actions,
  Subjects,
  TaskGroup,
  TaskGroupDispatchMethod,
  TaskGroupsListFilter,
} from '@dispatch/shared/types';
import {
  activeColumn,
  boldLinkColumn,
  CreateNewEntityButton,
  dateColumn,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';

export interface TaskGroupsProps {
  rows: PaginatedQueryResult<TaskGroup[], unknown>;
  onSearch: (value: string) => void;
  onFilterChange: (filters: TaskGroupsListFilter) => void;
  filters: TaskGroupsListFilter;
  permissions: Ability<[Actions, Subjects]> | undefined;
}

export function TaskGroups({
  rows,
  onSearch,
  onFilterChange,
  filters: listFilters,
  permissions,
}: TaskGroupsProps) {
  const { tableState, setTableState } = useTableState();
  const navigate = useNavigate();

  const formatDispatchMethod = (method: TaskGroupDispatchMethod) => {
    if (method === 'RoundRobin') {
      return 'Round Robin';
    }
    return method;
  };

  const columns: GridColDef[] = [
    boldLinkColumn<TaskGroup, string>(
      {
        field: 'name',
        headerName: 'Name',
        sortable: false,
      },
      (taskGroup, _rowValue) => `/admin/task-groups/${taskGroup?.id}`
    ),
    textColumn({
      field: 'description',
      headerName: 'Description',
      sortable: false,
    }),
    textColumn({
      field: 'dispatchMethod',
      headerName: 'Dispatch Method',
      valueGetter: (params) => formatDispatchMethod(params.row.dispatchMethod),
      sortable: false,
    }),
    textColumn({
      field: 'createdBy',
      headerName: 'Created By',
      sortable: false,
      valueGetter: (params) => params.row.createdBy,
    }),
    dateColumn({
      field: 'createdDate',
      headerName: 'Created Date',
      sortable: false,
      valueGetter: (params) => new Date(params.row.createdDate),
    }),
    textColumn({
      field: 'lastEditedBy',
      headerName: 'Edited By',
      sortable: false,
      valueGetter: (params) => params.row.lastEditedBy,
    }),
    dateColumn({
      field: 'lastEditedDate',
      headerName: 'Edited Date',
      sortable: false,
      valueGetter: (params) => new Date(params.row.lastEditedDate),
    }),
    activeColumn({
      field: 'isActive',
      headerName: 'Active',
      valueGetter: (params) => !params.row.deactivatedAt,
      sortable: false,
    }),
  ];

  const tableArgs: TableProps<TaskGroup> = {
    tableState: {
      columnModel: tableState['taskGroups'].columnModel,
      pinnedColumns: {
        ...tableState['taskGroups'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        taskGroups: { ...tableState['taskGroups'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        taskGroups: { ...tableState['taskGroups'], pinnedColumns: newPinned },
      });
    },
    rows: rows?.data?.filter((taskGroup: TaskGroup) => !!taskGroup.name) || [],
    columns,
    emptyEntityName: 'Task Group',
    status: rows?.status,
    serverPaging: {
      ...rows,
      onPageSizeChange: (newPageSize) => {
        onFilterChange({ ...listFilters, pageSize: newPageSize });
      },
    },
  };

  return (
    <PageComponent
      title="Task Groups"
      onSearch={onSearch}
      searchPlaceholder="Name"
      actions={
        permissions?.can?.('upsert', 'task-group') && (
          <CreateNewEntityButton
            onClick={() => navigate('/admin/task-groups/add')}
            entityName="Task Group"
          />
        )
      }
    >
      <Box width="100%">
        <TableComponent {...tableArgs} />
      </Box>
    </PageComponent>
  );
}

export default TaskGroups;
